import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import UserStatusIndicator from '../../components/ui/UserStatusIndicator';
import CallStatusWidget from '../../components/ui/CallStatusWidget';
import DialerKeypad from './components/DialerKeypad';
import AgentStatusControls from './components/AgentStatusControls';
import ActiveCallPanel from './components/ActiveCallPanel';
import RecentCallsList from './components/RecentCallsList';
import ContactDirectory from './components/ContactDirectory';
import CallQueueWidget from './components/CallQueueWidget';
import PerformanceMetrics from './components/PerformanceMetrics';

const AgentDashboard = () => {
  const [agentStatus, setAgentStatus] = useState('available');
  const [dialedNumber, setDialedNumber] = useState('');
  const [activeCall, setActiveCall] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isCallActive, setIsCallActive] = useState(false);

  // Mock active call data
  const mockCallData = {
    customerName: "Ahmet Yılmaz",
    phoneNumber: "+90 532 123 4567",
    email: "ahmet.yilmaz@email.com",
    location: "İstanbul, Türkiye",
    callType: "Gelen",
    priority: "Yüksek"
  };

  // Call duration timer
  useEffect(() => {
    let interval;
    if (isCallActive && activeCall) {
      interval = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    } else {
      setCallDuration(0);
    }

    return () => clearInterval(interval);
  }, [isCallActive, activeCall]);

  const handleStatusChange = (newStatus, breakType = null) => {
    setAgentStatus(newStatus);
    console.log(`Agent status changed to: ${newStatus}`, breakType ? `Break type: ${breakType}` : '');
  };

  const handleNumberChange = (number) => {
    setDialedNumber(number);
  };

  const handleCall = (number) => {
    if (agentStatus !== 'available') {
      alert('Arama yapmak için durumunuzu "Müsait" olarak ayarlayın.');
      return;
    }

    setActiveCall({
      ...mockCallData,
      phoneNumber: number,
      customerName: "Yeni Müşteri"
    });
    setIsCallActive(true);
    setDialedNumber('');
    console.log(`Calling: ${number}`);
  };

  const handleCallAction = (action, value = null) => {
    switch (action) {
      case 'mute':
        console.log(`Call ${value ? 'muted' : 'unmuted'}`);
        break;
      case 'hold':
        console.log(`Call ${value ? 'on hold' : 'resumed'}`);
        break;
      case 'record':
        console.log(`Recording ${value ? 'started' : 'stopped'}`);
        break;
      case 'transfer': console.log('Call transfer initiated');
        break;
      case 'end':
        setActiveCall(null);
        setIsCallActive(false);
        setCallDuration(0);
        console.log('Call ended');
        break;
      default:
        console.log(`Unknown call action: ${action}`);
    }
  };

  const handleRedial = (phoneNumber) => {
    setDialedNumber(phoneNumber);
    handleCall(phoneNumber);
  };

  const handleViewDetails = (callId) => {
    console.log(`Viewing details for call ID: ${callId}`);
  };

  const handleCallContact = (contact) => {
    if (agentStatus !== 'available') {
      alert('Arama yapmak için durumunuzu "Müsait" olarak ayarlayın.');
      return;
    }

    setActiveCall({
      customerName: contact?.name,
      phoneNumber: contact?.phone,
      email: contact?.email,
      location: contact?.company,
      callType: "Giden",
      priority: "Orta"
    });
    setIsCallActive(true);
    console.log(`Calling contact: ${contact?.name}`);
  };

  const handleAcceptCall = (call) => {
    if (agentStatus !== 'available') {
      alert('Aramayı kabul etmek için durumunuzu "Müsait" olarak ayarlayın.');
      return;
    }

    setActiveCall({
      customerName: call?.customerName,
      phoneNumber: call?.phoneNumber,
      email: "customer@email.com",
      location: "İstanbul, Türkiye",
      callType: "Gelen",
      priority: call?.priority
    });
    setIsCallActive(true);
    console.log(`Accepted call from: ${call?.customerName}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      <UserStatusIndicator />
      <CallStatusWidget />
      
      <div className="content-offset px-4 sm:px-6 lg:px-8 pb-8">
        <div className="max-w-full mx-auto">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Ajan Paneli</h1>
            <p className="text-lg text-slate-600">
              Arama yönetimi ve performans takibi için ana çalışma alanınız
            </p>
          </div>

          {/* Main Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column - Dialer & Status */}
            <div className="lg:col-span-3 space-y-6">
              <DialerKeypad
                onNumberChange={handleNumberChange}
                onCall={handleCall}
                dialedNumber={dialedNumber}
                isCallActive={isCallActive}
              />
              
              <AgentStatusControls
                currentStatus={agentStatus}
                onStatusChange={handleStatusChange}
              />
            </div>

            {/* Center Column - Active Call & Recent Calls */}
            <div className="lg:col-span-6 space-y-6">
              <ActiveCallPanel
                callData={activeCall}
                onCallAction={handleCallAction}
                callDuration={callDuration}
              />
              
              <RecentCallsList
                onRedial={handleRedial}
                onViewDetails={handleViewDetails}
              />
            </div>

            {/* Right Column - Contacts & Queue */}
            <div className="lg:col-span-3 space-y-6">
              <ContactDirectory
                onCallContact={handleCallContact}
              />
              
              <CallQueueWidget
                onAcceptCall={handleAcceptCall}
              />
            </div>
          </div>

          {/* Bottom Section - Performance Metrics */}
          <div className="mt-8">
            <PerformanceMetrics />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDashboard;